import React, { useState, useRef, useEffect, useCallback } from "react";
import { Check, ChevronDown, MapPin, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";

interface Suggestion {
  properties: {
    formatted: string;
  };
}

interface APIAddressSelectProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  icon?: React.ReactNode;
}

const GEOAPIFY_API_KEY = process.env.REACT_APP_GEOAPIFY_KEY || "";

export const APIAddressSelect: React.FC<APIAddressSelectProps> = ({
  value,
  onChange,
  placeholder = "Enter address...",
  className,
  error,
  icon
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Debounce logic
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  const fetchSuggestions = useCallback((text: string) => {
    if (text.length < 1) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    fetch(`https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(text)}&apiKey=${GEOAPIFY_API_KEY}&filter=countrycode:uk&limit=5`, {
      mode: 'cors'
    })
      .then(res => res.json())
      .then(data => {
        if (data.features) {
          setSuggestions(data.features);
        } else {
          setSuggestions([]);
        }
      })
      .catch(err => {
        console.error("Geoapify error:", err);
        setSuggestions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(newValue);
    }, 300); // 300ms debounce
  };

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef]);

  const handleSelectOption = (address: string) => {
    onChange(address);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && isOpen && suggestions.length > 0) {
      e.preventDefault();
      handleSelectOption(suggestions[highlightedIndex].properties.formatted);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className={cn("relative w-full group", className)} ref={wrapperRef}>
      <div className="relative">
        {icon && (
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors pointer-events-none">
            {icon}
          </div>
        )}
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            if (value.length >= 1 && suggestions.length === 0) {
              fetchSuggestions(value);
            }
          }}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          className={cn(
            "w-full h-[46px] sm:h-[48px] py-0 pr-10 bg-gray-50/50 border rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400",
            icon ? "pl-11" : "pl-4",
            error ? "border-red-500 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]" : "border-gray-100"
          )}
          autoComplete="off"
        />
        {/* Loading Indicator or Chevron */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {loading ? (
            <Loader2 className="h-4 w-4 text-[#885ABB] animate-spin" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400 opacity-50" />
          )}
        </div>
      </div>

      {isOpen && (suggestions.length > 0 || loading || (value.length >= 1 && suggestions.length === 0)) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-100 rounded-lg shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] max-h-60 overflow-y-auto">
          {loading && suggestions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> searching...
            </div>
          ) : suggestions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No address found.
            </div>
          ) : (
            <div className="py-1">
              {suggestions.map((option, index) => (
                <div
                  key={index}
                  className={cn(
                    "px-4 py-2.5 text-sm cursor-pointer flex items-center justify-between transition-colors",
                    index === highlightedIndex ? "bg-purple-50 text-[#885ABB]" : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                  )}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelectOption(option.properties.formatted)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <span className="truncate">{option.properties.formatted}</span>
                  {value === option.properties.formatted && (
                    <Check className="h-4 w-4 text-[#885ABB] flex-shrink-0 ml-2" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
